<template>
  <div class="container-fluid py-3">
    <ToastNotification ref="toastComponent" />


    <div class="row g-3">
      <DashboardCard
        title="Time Overview"
        icon="bi-clock-history"
        section="timeOverview"
        :isLoading="['time', 'all'].includes(isLoading)"
        @refresh="refreshData('time')"
      >
        <template #content>
          <GenericList
            :data="timeData"
            :isLoading="['time', 'all'].includes(isLoading)"
            :skeletonCount="6"
            :getItemClass="
              (item) => getTimeBorderClass(item?.offTime, [600, 10800])
            "
          >
            <template #item="{ item }">
              <div class="row">
                <div class="col-md-8 col-sm-12 align-items-start">
                  <div>
                    <strong>{{ item.id }} {{ item.name }}</strong>
                  </div>
                  <span class="text-muted">
                    <i class="bi bi-lightning"></i> ACT: {{ item.act }}
                  </span>
                </div>
                <div class="col-md-4 flex-column align-items-end">
                  <div>
                    <span class="text-muted" title="Last seen">
                      <i class="bi bi-eye"></i> {{ item.offTime }}
                    </span>
                  </div>
                  <div>
                    <span class="text-muted" title="Off time">
                      <i class="bi bi-clock"></i> {{ item.lastSeen }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </GenericList>
        </template>
      </DashboardCard>

      <DashboardCard
        title="Photos"
        icon="bi-images"
        section="photosSection"
        :isLoading="isPhotosLoading"
        @refresh="refreshData('photos')"
      >
        <template #content>
          <GenericList
            :data="photosData"
            :isLoading="isPhotosLoading"
            :skeletonCount="7"
            :getItemClass="
              (item) =>
                'border-' +
                getBadgeClass(item.count, [1, 10]).replace('bg-', '')
            "
          >
            <template #item="{ item }">
              <div
                class="d-flex justify-content-between align-items-center w-100"
              >
                <span><strong>{{ item.id }} {{ item.name }}</strong></span>
                <span :class="getBadgeClass(item.count, [1, 10])" class="badge">
                  {{ item.count }}
                </span>
              </div>
            </template>
          </GenericList>
        </template>
      </DashboardCard>

      <DashboardCard
        title="Paper"
        icon="bi-file-text"
        section="paperSection"
        :isLoading="['paper', 'all'].includes(isLoading)"
        @refresh="refreshData('paper')"
      >
        <template #content>
          <GenericList
            :data="paperData"
            :isLoading="['paper', 'all'].includes(isLoading)"
            :skeletonCount="7"
            :getItemClass="
              (item) =>
                'border-' +
                getBadgeClass(item.count, [10, 50]).replace('bg-', '')
            "
          >
            <template #item="{ item }">
              <div
                class="d-flex justify-content-between align-items-center w-100"
              >
                <span><strong>{{ item.id }} {{ item.name }}</strong></span>
                <span
                  :class="getBadgeClass(item.count, [10, 50])"
                  class="badge"
                >
                  {{ item.count }}
                </span>
              </div>
            </template>
          </GenericList>
        </template>
      </DashboardCard>
    </div>

    <div class="row g-3">
      <div class="col-12">
        <DashboardCard
          title="Messages"
          icon="bi-journal-text"
          section="messagesSection"
          :isLoading="['messages', 'all'].includes(isLoading)"
          :isLarge="true"
          @refresh="refreshData('messages')"
        >
          <template #content>
            <MessageList
              :data="messagesData"
              :isLoading="['messages', 'all'].includes(isLoading)"
              @delete="confirmDelete"
            />
          </template>
        </DashboardCard>
      </div>
    </div>

        <div
      v-if="canViewExecutiveTopCards"
      class="row g-3 align-items-stretch top-overview-row mb-3"
    >
      <div class="col-12 col-lg-6">
        <div class="card shadow-sm border-0 h-100 compact-top-card dashboard-top-card">
          <div class="card-body dashboard-top-card-body">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <div class="urgent-strip-title">
                <i class="bi bi-exclamation-triangle me-1"></i>
                Urgent Performance Monitor
              </div>
              <span class="small text-muted">Live status from dashboard feed</span>
            </div>

            <div v-if="isTopOverviewLoading" class="urgent-compact-grid">
              <div
                v-for="index in 6"
                :key="`urgent-skeleton-${index}`"
                class="urgent-compact-item urgent-compact-skeleton"
              >
                <div class="skeleton-line skeleton-line-title"></div>
                <div class="skeleton-line skeleton-line-sub"></div>
                <div class="skeleton-line skeleton-line-sub short"></div>
              </div>
            </div>
            <div v-else class="urgent-compact-grid">
              <div
                v-for="card in urgentCards"
                :key="card.key"
                class="urgent-compact-item"
                :class="`urgent-metric-${card.tone}`"
              >
                <div class="urgent-compact-head">
                  <span class="urgent-metric-icon">
                    <i class="bi" :class="card.icon"></i>
                  </span>
                  <span class="urgent-metric-label">{{ card.title }}</span>
                  <span class="urgent-compact-value">{{ card.value }}</span>
                </div>
                <div class="urgent-compact-detail">{{ card.detail }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-6">
        <div class="card shadow-sm border-0 h-100 compact-top-card dashboard-top-card">
          <div class="card-header dashboard-top-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h5 class="mb-0">
              <i class="bi bi-graph-up-arrow me-1"></i>
              Business Analytics Quick Overview
            </h5>
            <div class="d-flex align-items-center gap-2">
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="isBusinessQuickLoading"
                @click="refreshData('businessQuick')"
              >
                <span
                  v-if="isBusinessQuickLoading"
                  class="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <i v-else class="bi bi-arrow-clockwise"></i>
              </button>
              <router-link class="btn btn-sm btn-outline-primary" :to="{ name: 'RevenueAnalytics' }">
                <i class="bi bi-arrow-up-right-square me-1"></i>
                Open Full Analytics
              </router-link>
            </div>
          </div>

          <div class="card-body dashboard-top-card-body">
            <div class="business-quick-wrap">
              <div class="small text-muted mb-2">
                Range: {{ businessQuickRangeLabel }}
              </div>

              <template v-if="isBusinessQuickLoading && !businessQuickData">
                <div class="business-quick-skeleton">
                  <div class="skeleton-line skeleton-line-title"></div>
                  <div class="skeleton-line skeleton-line-sub"></div>
                  <div class="skeleton-line skeleton-line-sub"></div>
                  <div class="skeleton-line skeleton-line-sub short"></div>
                </div>
              </template>

              <div v-else-if="businessQuickError" class="small text-danger mb-2">
                {{ businessQuickError }}
              </div>

              <template v-else-if="businessQuickData">
                <div class="business-quick-row">
                  <span class="business-quick-label">
                    <i class="bi bi-cash-stack me-1"></i>
                    Revenue
                  </span>
                  <span class="business-quick-value">
                    {{ formatMoney(businessQuickData.snapshot?.total_revenue) }}
                  </span>
                  <span
                    class="business-quick-trend"
                    :class="getQuickTrendClass(businessQuickRevenueTrend?.direction)"
                    :title="getQuickTrendTooltip(businessQuickRevenueTrend?.delta_pct)"
                  >
                    {{ getQuickTrendText(businessQuickRevenueTrend?.delta_pct, businessQuickRevenueTrend?.direction) }}
                  </span>
                </div>

                <div class="business-quick-row">
                  <span class="business-quick-label">
                    <i class="bi bi-trophy me-1"></i>
                    Best Device
                  </span>
                  <span class="business-quick-value">
                    {{ businessQuickData.snapshot?.best_device?.device_name || "N/A" }}
                  </span>
                  <span class="business-quick-meta">
                    {{ formatMoney(businessQuickData.snapshot?.best_device?.revenue) }}
                  </span>
                </div>

                <div class="business-quick-row">
                  <span class="business-quick-label">
                    <i class="bi bi-exclamation-triangle me-1"></i>
                    Worst Device
                  </span>
                  <span class="business-quick-value">
                    {{ businessQuickData.snapshot?.worst_device?.device_name || "N/A" }}
                  </span>
                  <span class="business-quick-meta">
                    {{ formatMoney(businessQuickData.snapshot?.worst_device?.revenue) }}
                  </span>
                </div>

                <div class="business-quick-row">
                  <span class="business-quick-label">
                    <i class="bi bi-clock-history me-1"></i>
                    Peak Hour
                  </span>
                  <span class="business-quick-value">
                    {{ businessQuickData.snapshot?.peak_hour?.label || "N/A" }}
                  </span>
                  <span class="business-quick-meta">
                    {{ formatMoney(businessQuickData.snapshot?.peak_hour?.revenue) }}
                  </span>
                </div>
              </template>

              <div v-else class="small text-muted mb-2">
                No business analytics data yet.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


    <BootstrapModal
        ref="deleteConfirmModal"
        modalId="deleteMessageModal"
        title="Confirm Delete"
        confirmText="Delete"
        @confirm="deleteMessage"
      >
        <template #body>
          Are you sure you want to delete this message?
        </template>
      </BootstrapModal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import emitter from "../eventBus";
import ToastNotification from "../components/Common/ToastNotification.vue";
import DashboardService from "@/services/dashboardService.js";
import DashboardCard from "@/components/Common/DashboardCard.vue";
import GenericList from "@/components/Common/GenericList.vue";
import MessageList from "@/components/Common/MessageList.vue";
import MessageService from "@/services/messageService.js";
import BootstrapModal from "@/components/Common/BootstrapModal.vue";
import analyticsService from "@/services/analyticsService";

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultAnalyticsRange = () => {
  const toDate = new Date();
  const fromDate = new Date(toDate);
  fromDate.setDate(fromDate.getDate() - 7);

  return {
    from: formatDateForInput(fromDate),
    to: formatDateForInput(toDate),
  };
};

export default {
  components: {
    BootstrapModal,
    DashboardCard,
    ToastNotification,
    GenericList,
    MessageList,
  },
  data() {
    return {
      isLoading: false,
      dashboardData: null,
      worker: null,
      timeData: null,
      photosData: null,
      paperData: null,
      messagesData: null,
      messageToDelete: null,
      businessQuickData: null,
      businessQuickError: "",
      businessQuickRangeLabel: "",
    };
  },
  computed: {
    ...mapGetters("cards", ["isCardCollapsed"]),
    isPhotosLoading() {
      return ["photos", "all"].includes(this.isLoading);
    },
    isTimeLoading() {
      return ["time", "all"].includes(this.isLoading);
    },
    isPaperLoading() {
      return ["paper", "all"].includes(this.isLoading);
    },
    isMessagesLoading() {
      return ["messages", "all"].includes(this.isLoading);
    },
    isBusinessQuickLoading() {
      return ["businessQuick", "all"].includes(this.isLoading);
    },
    currentUserRole() {
      const role = this.$store.getters["auth/getUser"]?.role;
      if (typeof role === "string") {
        return role;
      }

      return role?.name || "";
    },
    canViewExecutiveTopCards() {
      return this.currentUserRole !== "device_maintainer";
    },
    isTopOverviewLoading() {
      return this.isLoading === "all" && !this.dashboardData;
    },
    businessQuickRevenueTrend() {
      return this.businessQuickData?.trend_indicator?.metrics?.revenue_total || null;
    },
    safeTimeData() {
      return Array.isArray(this.timeData) ? this.timeData : [];
    },
    safePhotosData() {
      return Array.isArray(this.photosData) ? this.photosData : [];
    },
    safePaperData() {
      return Array.isArray(this.paperData) ? this.paperData : [];
    },
    safeMessagesData() {
      return Array.isArray(this.messagesData) ? this.messagesData : [];
    },
    urgentMetrics() {
      const activeStatusRows = this.safeTimeData.filter(
        (item) => this.hasActiveFlag(item) && this.extractDeviceId(item)
      );
      const shouldFilterInactive = activeStatusRows.length > 0;
      const activeDeviceIds = new Set(
        activeStatusRows
          .filter((item) => this.isDeviceActive(item))
          .map((item) => this.extractDeviceId(item))
          .filter(Boolean)
      );
      const includeActiveOnly = (item) => this.isIncludedInRiskMetrics(item, activeDeviceIds, shouldFilterInactive);

      const filteredTimeRows = this.safeTimeData.filter(includeActiveOnly);
      const filteredPaperRows = this.safePaperData.filter(includeActiveOnly);
      const filteredPhotoRows = this.safePhotosData.filter(includeActiveOnly);

      const offline3hRows = filteredTimeRows.filter(
        (item) => this.parseOffTimeSeconds(item?.offTime) > 10800
      );
      const offline24hRows = filteredTimeRows.filter(
        (item) => this.parseOffTimeSeconds(item?.offTime) > 86400
      );

      const outOfPaperRows = filteredPaperRows.filter(
        (item) => this.toNumber(item?.count) <= 0
      );
      const lowPaperRows = filteredPaperRows.filter(
        (item) => this.toNumber(item?.count) > 0 && this.toNumber(item?.count) <= 10
      );

      const zeroPhotoRows = filteredPhotoRows.filter(
        (item) => this.toNumber(item?.count) <= 0
      );
      const lowPhotoRows = filteredPhotoRows.filter(
        (item) => this.toNumber(item?.count) > 0 && this.toNumber(item?.count) < 3
      );

      const errorRows = this.safeMessagesData.filter(
        (item) =>
          String(item?.type || "").toUpperCase() === "ERROR" &&
          includeActiveOnly(item)
      );

      const allDeviceIds = new Set();
      [...filteredTimeRows, ...filteredPaperRows, ...filteredPhotoRows].forEach(
        (item) => {
          const id = this.extractDeviceId(item);
          if (id) {
            allDeviceIds.add(id);
          }
        }
      );

      const atRiskDeviceIds = new Set();
      [...offline3hRows, ...outOfPaperRows, ...zeroPhotoRows].forEach((item) => {
        const id = this.extractDeviceId(item);
        if (id) {
          atRiskDeviceIds.add(id);
        }
      });

      const criticalDeviceIds = new Set();
      [...offline24hRows, ...outOfPaperRows].forEach((item) => {
        const id = this.extractDeviceId(item);
        if (id) {
          criticalDeviceIds.add(id);
        }
      });

      const totalDeviceCount = allDeviceIds.size;
      const atRiskDevicesCount = atRiskDeviceIds.size;
      const healthyDevicesCount = Math.max(0, totalDeviceCount - atRiskDevicesCount);
      const riskRatePct = totalDeviceCount ? (atRiskDevicesCount / totalDeviceCount) * 100 : 0;
      const healthyRatePct = totalDeviceCount ? (healthyDevicesCount / totalDeviceCount) * 100 : 0;

      return {
        offline3hCount: offline3hRows.length,
        offline24hCount: offline24hRows.length,
        offlineSample: this.formatSampleNames(offline24hRows.length ? offline24hRows : offline3hRows),
        outOfPaperCount: outOfPaperRows.length,
        lowPaperCount: lowPaperRows.length,
        outOfPaperSample: this.formatSampleNames(outOfPaperRows),
        zeroPhotoCount: zeroPhotoRows.length,
        lowPhotoCount: lowPhotoRows.length,
        zeroPhotoSample: this.formatSampleNames(zeroPhotoRows),
        errorCount: errorRows.length,
        atRiskDevicesCount,
        criticalDevicesCount: criticalDeviceIds.size,
        totalDeviceCount,
        healthyDevicesCount,
        riskRatePct,
        healthyRatePct,
        inactiveFiltered: shouldFilterInactive,
      };
    },
    urgentCards() {
      const metrics = this.urgentMetrics;

      return [
        {
          key: "fleetHealth",
          title: "Fleet Health",
          value: this.formatPct(metrics.healthyRatePct),
          detail: `Healthy devices: ${this.formatInt(metrics.healthyDevicesCount)} / ${this.formatInt(metrics.totalDeviceCount)}`,
          icon: "bi-heart-pulse",
          tone:
            metrics.healthyRatePct >= 85
              ? "good"
              : metrics.healthyRatePct >= 65
                ? "warning"
                : "danger",
        },
        {
          key: "critical",
          title: "Critical Devices",
          value: this.formatInt(metrics.criticalDevicesCount),
          detail: `Offline >24h + out of paper`,
          icon: "bi-exclamation-diamond",
          tone: metrics.criticalDevicesCount > 0 ? "danger" : "good",
        },
        {
          key: "atRisk",
          title: "Devices At Risk",
          value: this.formatInt(metrics.atRiskDevicesCount),
          detail: `Risk rate: ${this.formatPct(metrics.riskRatePct)} across offline/paper/photo checks${metrics.inactiveFiltered ? " (active only)" : ""}`,
          icon: "bi-exclamation-octagon",
          tone: metrics.atRiskDevicesCount > 0 ? "danger" : "good",
        },
        {
          key: "offline",
          title: "Offline Over 3h",
          value: this.formatInt(metrics.offline3hCount),
          detail: `Over 24h: ${this.formatInt(metrics.offline24hCount)}${metrics.offlineSample ? ` (${metrics.offlineSample})` : ""}`,
          icon: "bi-wifi-off",
          tone: metrics.offline3hCount > 0 ? "danger" : "good",
        },
        {
          key: "paper",
          title: "Paper Risk",
          value: this.formatInt(metrics.outOfPaperCount),
          detail: `Low (<=10): ${this.formatInt(metrics.lowPaperCount)}${metrics.outOfPaperSample ? ` (${metrics.outOfPaperSample})` : ""}`,
          icon: "bi-stack",
          tone: metrics.outOfPaperCount > 0 ? "danger" : metrics.lowPaperCount > 0 ? "warning" : "good",
        },
        {
          key: "photos",
          title: "Photo Output Risk",
          value: this.formatInt(metrics.zeroPhotoCount),
          detail: `Very low (1-2): ${this.formatInt(metrics.lowPhotoCount)}${metrics.zeroPhotoSample ? ` (${metrics.zeroPhotoSample})` : ""}`,
          icon: "bi-camera2",
          tone: metrics.zeroPhotoCount > 0 ? "warning" : "good",
        },
        {
          key: "errors",
          title: "Error Messages",
          value: this.formatInt(metrics.errorCount),
          detail: `Log events currently marked ERROR`,
          icon: "bi-bug",
          tone: metrics.errorCount > 0 ? "warning" : "good",
        },
      ];
    },
  },
  mounted() {
    this.isLoading = "all";
    if (this.canViewExecutiveTopCards) {
      this.fetchBusinessQuickOverview();
    }

    if (window.Worker) {
      let token = localStorage.getItem("token");
      const apiUrl = process.env.VUE_APP_API_BASE_URL;
      const apiInterval =
        Number(process.env.VUE_APP_DASHBOARD_WORKER_TIMING) || 30000;

      this.worker = new Worker(
        new URL("../workers/dashboardWorker.js", import.meta.url)
      );
      this.worker.postMessage({ apiUrl, interval: apiInterval, token });

      this.worker.onmessage = (event) => {
        if (event.data.showToast) {
          emitter.emit("show-toast", {
            message: event.data.error,
            type: "danger",
          });
        }

        if (event.data.refreshToken) {
          token = event.data.refreshToken;
          localStorage.setItem("token", token);
          this.worker.postMessage({ apiUrl, interval: apiInterval, token });
        }

        if (event.data.logout) {
          this.handleLogout();
        }

        if (event.data.success) {
          this.dashboardData = event.data.data.data;
          this.timeData = this.dashboardData.timeOverview;
          this.photosData = this.dashboardData.photosOverview;
          this.paperData = this.dashboardData.paperOverview;
          this.messagesData = this.dashboardData.logOverview;

          this.$nextTick(() => {
            this.isLoading = "";
          });
        } else {
          console.error("Dashboard Worker Error:", event.data.error);
        }
      };
    }
  },
  beforeUnmount() {
    if (this.worker) {
      this.worker.terminate();
    }
  },
  methods: {
    ...mapActions("cards", ["toggleCard"]),
    async handleLogout() {
      await this.$store.dispatch("auth/logout");
      this.$router.push({ name: "LoginPage" });
    },
    confirmDelete(msg) {
      this.messageToDelete = msg;
      this.$refs.deleteConfirmModal.show();
    },

    async deleteMessage() {
      try {
        if (!this.messageToDelete || !this.messageToDelete.id) return;
        const success = await MessageService.deleteMessage(this.messageToDelete.id);
        if (success) {
            this.$refs.deleteConfirmModal.hide();

            emitter.emit("show-toast", {
                message: `Message deleted successfully.`,
                type: "success",
            });
        }
        this.refreshData("messages");
        this.messageToDelete = null;
      } catch (error) {
        console.error("Failed to delete message:", error);
      }
    },
    async refreshData(type) {
      this.isLoading = type;

      try {
        switch (type) {
          case "time":
            this.timeData = await DashboardService.getTimeOverview();
            break;
          case "photos":
            this.photosData = await DashboardService.getPhotosOverview();
            break;
          case "paper":
            this.paperData = await DashboardService.getPaperOverview();
            break;
          case "messages":
            this.messagesData = await DashboardService.getLogOverview();
            break;
          case "businessQuick":
            await this.fetchBusinessQuickOverview();
            break;
        }
      } catch (error) {
        console.error(`Error fetching ${type} data:`, error);
        emitter.emit("show-toast", {
          message:
            error.customMessage || this.$t("errors.failed_to_fetch_data"),
          type: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchBusinessQuickOverview() {
      const range = getDefaultAnalyticsRange();
      this.businessQuickRangeLabel = `${range.from} -> ${range.to}`;
      this.businessQuickError = "";

      try {
        const payload = await analyticsService.getExecutiveSummary(range);
        this.businessQuickData = payload || null;
      } catch (error) {
        this.businessQuickData = null;
        this.businessQuickError = error?.customMessage || error?.message || "Failed to load business analytics";
      }
    },
    toNumber(value) {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : 0;
    },
    formatInt(value) {
      return this.toNumber(value).toLocaleString();
    },
    formatMoney(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return "N/A";
      }

      return `${numeric.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} GEL`;
    },
    formatPct(value, digits = 0) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return "0%";
      }

      return `${numeric.toFixed(digits)}%`;
    },
    getQuickTrendText(deltaPct, direction) {
      const delta = Number(deltaPct);
      if (!Number.isFinite(delta)) {
        return "No baseline";
      }

      const arrow = direction === "up" ? "▲" : direction === "down" ? "▼" : "■";
      const value = delta > 0 ? `+${delta.toFixed(1)}%` : `${delta.toFixed(1)}%`;
      return `${arrow} ${value}`;
    },
    getBusinessQuickComparisonLabel() {
      const previous = this.businessQuickData?.trend_indicator?.previous_period;
      if (!previous?.from || !previous?.to) {
        return "Compared to previous period: N/A";
      }

      return `Compared to previous period: ${previous.from} -> ${previous.to}`;
    },
    getQuickTrendTooltip(deltaPct) {
      const parts = [this.getBusinessQuickComparisonLabel()];
      if (!Number.isFinite(Number(deltaPct))) {
        parts.push("No previous baseline");
      }

      return parts.filter(Boolean).join(" | ");
    },
    getQuickTrendClass(direction) {
      if (direction === "up") {
        return "business-quick-trend-up";
      }

      if (direction === "down") {
        return "business-quick-trend-down";
      }

      return "business-quick-trend-flat";
    },
    formatSampleNames(rows, limit = 2) {
      if (!Array.isArray(rows) || !rows.length) {
        return "";
      }

      return rows
        .slice(0, limit)
        .map((item) => {
          const id = item?.id ?? item?.deviceId ?? item?.device_id;
          const name = item?.name ?? item?.deviceName ?? item?.device_name;

          if (id !== null && id !== undefined && name) {
            return `#${id} ${name}`;
          }

          if (name) {
            return name;
          }

          if (id !== null && id !== undefined) {
            return `#${id}`;
          }

          return "";
        })
        .filter(Boolean)
        .join(", ");
    },
    extractDeviceId(item) {
      const raw = item?.id ?? item?.deviceId ?? item?.device_id;
      if (raw === null || raw === undefined || raw === "") {
        return null;
      }

      return String(raw);
    },
    hasActiveFlag(item) {
      return (
        Object.prototype.hasOwnProperty.call(item || {}, "act") ||
        Object.prototype.hasOwnProperty.call(item || {}, "active")
      );
    },
    isDeviceActive(item) {
      const raw = item?.active ?? item?.act;

      if (raw === null || raw === undefined || raw === "") {
        return true;
      }

      if (typeof raw === "boolean") {
        return raw;
      }

      const normalized = String(raw).trim().toLowerCase();

      if (["1", "true", "yes", "y", "on", "active"].includes(normalized)) {
        return true;
      }

      if (["0", "false", "no", "n", "off", "inactive"].includes(normalized)) {
        return false;
      }

      return true;
    },
    isIncludedInRiskMetrics(item, activeDeviceIds, shouldFilterInactive) {
      if (!shouldFilterInactive) {
        return true;
      }

      const id = this.extractDeviceId(item);
      if (!id) {
        return true;
      }

      return activeDeviceIds.has(id);
    },
    parseOffTimeSeconds(offTime) {
      if (!offTime || typeof offTime !== "string") {
        return 0;
      }

      const clean = offTime.trim().replace(",", "");

      let days = 0;
      let timeStr = "";

      const match = clean.match(/(?:(\d+)\s+days?\s+)?(\d{1,2}:\d{2}:\d{2})/i);
      if (match) {
        days = parseInt(match[1] || "0", 10);
        timeStr = match[2];
      }

      const [hours = 0, minutes = 0, seconds = 0] = timeStr
        .split(":")
        .map((chunk) => parseInt(chunk, 10) || 0);

      return days * 86400 + hours * 3600 + minutes * 60 + seconds;
    },
    getTimeBorderClass(offTime, limits = [600, 10800, 999999]) {
      const totalSeconds = this.parseOffTimeSeconds(offTime);
      return this.getBorderClass(totalSeconds, limits, true);
    },
    getBorderClass(value, limits, reverse = false) {
      const [low = 600, mid = 10800, high = 999999] = limits;

      if (reverse) {
        if (value <= low) return "border-success";
        if (value > low && value <= mid) return 'border-warning';
        return "border-danger";
      } else {
        if (value <= low) return "border-danger";
        if (value >= mid && value < high) return "border-warning";
        return "border-success";
      }
    },
    getBadgeClass(value, [mid, high]) {
      if (value < mid) return "bg-danger";
      if (value >= mid && value < high) return "bg-warning";
      if (value >= high) return "bg-success";
    },
  },
};
</script>

<style scoped>
.top-overview-row > div {
  display: flex;
}

.compact-top-card {
  width: 100%;
}

.dashboard-top-card {
  border-radius: 0.6rem;
}

.dashboard-top-card-header {
  padding: 0.55rem 0.75rem;
}

.dashboard-top-card-body {
  padding: 0.75rem;
}

.business-quick-wrap {
  min-height: 168px;
}

.business-quick-skeleton {
  display: grid;
  gap: 0.6rem;
  padding: 0.2rem 0.1rem;
}

.business-quick-row {
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(140px, 1.2fr) auto;
  gap: 0.45rem;
  align-items: center;
  padding: 0.26rem 0;
  border-bottom: 1px dashed #e2e8f0;
}

.business-quick-row:last-of-type {
  border-bottom: none;
}

.business-quick-label {
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 700;
}

.business-quick-value {
  font-size: 0.83rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.business-quick-meta {
  font-size: 0.76rem;
  color: #475569;
  white-space: nowrap;
}

.business-quick-trend {
  font-size: 0.76rem;
  font-weight: 700;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 0.1rem 0.45rem;
  white-space: nowrap;
}

.business-quick-trend-up {
  color: #166534;
  background: rgba(34, 197, 94, 0.12);
  border-color: rgba(34, 197, 94, 0.28);
}

.business-quick-trend-down {
  color: #b91c1c;
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.28);
}

.business-quick-trend-flat {
  color: #475569;
  background: rgba(148, 163, 184, 0.14);
  border-color: rgba(148, 163, 184, 0.32);
}

.urgent-strip-title {
  font-size: 0.96rem;
  font-weight: 700;
}

.urgent-compact-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.45rem;
}

.urgent-compact-item {
  border: 1px solid #e6eaf0;
  border-radius: 0.5rem;
  background: #f8f9fa;
  padding: 0.42rem 0.52rem;
  min-height: 70px;
}

.urgent-compact-skeleton {
  display: grid;
  gap: 0.42rem;
  align-content: center;
}

.skeleton-line {
  height: 0.78rem;
  border-radius: 0.35rem;
  background: linear-gradient(90deg, #e9ecef 0%, #f8f9fa 50%, #e9ecef 100%);
  background-size: 220% 100%;
  animation: dashboard-skeleton-shimmer 1.2s linear infinite;
}

.skeleton-line-title {
  width: 58%;
}

.skeleton-line-sub {
  width: 92%;
}

.skeleton-line-sub.short {
  width: 67%;
}

.urgent-compact-head {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.32rem;
  margin-bottom: 0.2rem;
}

.urgent-metric-icon {
  width: 1.18rem;
  height: 1.18rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.66rem;
}

.urgent-metric-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  color: #6c757d;
  font-weight: 700;
  line-height: 1.1;
}

.urgent-compact-value {
  font-size: 0.92rem;
  font-weight: 800;
  line-height: 1;
  color: #111827;
}

.urgent-compact-detail {
  font-size: 0.68rem;
  color: #64748b;
  line-height: 1.18;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.urgent-metric-danger {
  border-color: rgba(220, 53, 69, 0.35);
  background: linear-gradient(180deg, rgba(255, 235, 238, 0.72) 0%, #ffffff 100%);
}

.urgent-metric-danger .urgent-metric-icon {
  background: rgba(220, 53, 69, 0.14);
  border: 1px solid rgba(220, 53, 69, 0.32);
  color: #b91c1c;
}

.urgent-metric-warning {
  border-color: rgba(245, 158, 11, 0.34);
  background: linear-gradient(180deg, rgba(255, 246, 223, 0.7) 0%, #ffffff 100%);
}

.urgent-metric-warning .urgent-metric-icon {
  background: rgba(245, 158, 11, 0.15);
  border: 1px solid rgba(245, 158, 11, 0.32);
  color: #b45309;
}

.urgent-metric-good {
  border-color: rgba(34, 197, 94, 0.28);
  background: linear-gradient(180deg, rgba(236, 253, 245, 0.78) 0%, #ffffff 100%);
}

.urgent-metric-good .urgent-metric-icon {
  background: rgba(34, 197, 94, 0.14);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #166534;
}

@keyframes dashboard-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 768px) {
  .top-overview-row > div {
    display: block;
  }

  .business-quick-row {
    grid-template-columns: 1fr;
    gap: 0.22rem;
    padding: 0.35rem 0;
  }

  .business-quick-meta,
  .business-quick-trend {
    justify-self: start;
  }

  .urgent-compact-grid {
    grid-template-columns: 1fr;
  }

  .urgent-compact-item {
    min-height: 64px;
  }
}
</style>
